import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react'

interface UseKeepListPositionOptions {
  /** The scrolling word list. Each row carries its word in `data-word`. */
  listRef: RefObject<HTMLUListElement | null>
  /** The words in the order they are listed. */
  sortedWords: readonly string[]
  /** A word this user just added, which the list scrolls to instead. */
  addedWord: string | null
}

interface ListAnchor {
  word: string
  /** How far the row's top sits below the list's top, in pixels. */
  offset: number
}

function readAnchor(list: HTMLUListElement): ListAnchor | null {
  const listTop = list.getBoundingClientRect().top

  for (const row of list.querySelectorAll<HTMLLIElement>('li[data-word]')) {
    const { top, bottom } = row.getBoundingClientRect()

    if (bottom > listTop) return { word: row.dataset.word ?? '', offset: top - listTop }
  }

  return null
}

function rowFor(list: HTMLUListElement, word: string): HTMLLIElement | undefined {
  return [...list.querySelectorAll<HTMLLIElement>('li[data-word]')].find(
    (row) => row.dataset.word === word
  )
}

/**
 * Keeps the rows the user is looking at in place when the word list changes under them, for
 * example when another user adds a word that sorts above them (ED-6). Chrome, Edge and Firefox do
 * this on their own (CSS scroll anchoring); Safari does not, so the list would jump there. The
 * first row in view is remembered, and after the list changes it is put back at the same height.
 * At the very top nothing is held, as in the browsers that anchor, so a word added above shows.
 */
export function useKeepListPosition({
  listRef,
  sortedWords,
  addedWord,
}: UseKeepListPositionOptions) {
  const anchorRef = useRef<ListAnchor | null>(null)
  const previousWordsRef = useRef(sortedWords)

  // The list's `onScroll`: remembers the first row in view as the user scrolls.
  const rememberPosition = useCallback(() => {
    const list = listRef.current

    anchorRef.current = list && list.scrollTop > 0 ? readAnchor(list) : null
  }, [listRef])

  // Before paint, so a jump is never seen.
  useLayoutEffect(() => {
    const previousWords = previousWordsRef.current
    const anchor = anchorRef.current
    const { current } = listRef

    previousWordsRef.current = sortedWords

    if (!current || previousWords === sortedWords) return

    // The user's own add scrolls to the new word; holding the old place would undo that.
    const isOwnAdd =
      addedWord !== null && sortedWords.includes(addedWord) && !previousWords.includes(addedWord)
    const row = anchor && !isOwnAdd ? rowFor(current, anchor.word) : undefined

    if (anchor && row) {
      const offset = row.getBoundingClientRect().top - current.getBoundingClientRect().top

      current.scrollTop += offset - anchor.offset
    }

    rememberPosition()
  }, [sortedWords, addedWord, listRef, rememberPosition])

  return rememberPosition
}
